import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { useLeadSubmit, ConsentCheckbox, Honeypot, FieldError } from "@/components/forms/shared";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TIME_BLOCKS = ["Morning (9 AM – 12 PM)", "Afternoon (12 PM – 4 PM)", "Evening (4 PM – 6 PM)"];

export default function ScheduleForm() {
  const { submit, submitting } = useLeadSubmit("schedule");
  const [form, setForm] = useState({ name: "", phone: "", email: "", preferred_days: [], time_block: "", notes: "" });
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [errors, setErrors] = useState({});

  const toggleDay = (d) => {
    setForm((p) => ({ ...p, preferred_days: p.preferred_days.includes(d) ? p.preferred_days.filter((x) => x !== d) : [...p.preferred_days, d] }));
    setErrors((p) => ({ ...p, preferred_days: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.phone.trim()) e.phone = "Please enter your phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.preferred_days.length) e.preferred_days = "Please choose at least one day.";
    if (!form.time_block) e.time_block = "Please choose a time of day.";
    if (!consent) e.consent = "Please confirm your consent so we may contact you.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <form className="relative rounded-2xl border border-navy-100 bg-white p-6 sm:p-8 shadow-lg"
      onSubmit={(e) => { e.preventDefault(); if (validate()) submit(form, { consent, company }); }}
      noValidate data-testid="schedule-form">
      <Honeypot value={company} onChange={setCompany} />
      <div className="flex items-center gap-3">
        <CalendarDays className="h-6 w-6 text-gold-dark" aria-hidden="true" />
        <h2 className="text-2xl tracking-tight">Pick a Good Time to Talk</h2>
      </div>
      <p className="mt-2 text-mutedink">Choose the days and time that work best for you, and someone will be in contact soon to confirm.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="sf-name">Name</label>
          <input id="sf-name" className="field-input" autoComplete="name" value={form.name}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors((p) => ({ ...p, name: "" })); }} data-testid="schedule-name" />
          <FieldError message={errors.name} />
        </div>
        <div>
          <label className="field-label" htmlFor="sf-phone">Phone</label>
          <input id="sf-phone" type="tel" className="field-input" autoComplete="tel" value={form.phone}
            onChange={(e) => { setForm({ ...form, phone: e.target.value }); setErrors((p) => ({ ...p, phone: "" })); }} data-testid="schedule-phone" />
          <FieldError message={errors.phone} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="sf-email">Email</label>
          <input id="sf-email" type="email" className="field-input" autoComplete="email" value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors((p) => ({ ...p, email: "" })); }} data-testid="schedule-email" />
          <FieldError message={errors.email} />
        </div>
      </div>

      <fieldset className="mt-6">
        <legend className="field-label">Which days work best? (Choose any)</legend>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {DAYS.map((d) => (
            <label key={d} className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border px-3 py-3 font-semibold min-h-[48px] transition-colors ${
              form.preferred_days.includes(d) ? "border-navy bg-navy text-white" : "border-input bg-white text-ink hover:border-navy"
            }`} data-testid={`schedule-day-${d.toLowerCase()}`}>
              <input type="checkbox" className="sr-only" checked={form.preferred_days.includes(d)} onChange={() => toggleDay(d)} />
              {d}
            </label>
          ))}
        </div>
        <FieldError message={errors.preferred_days} />
      </fieldset>

      <fieldset className="mt-6">
        <legend className="field-label">Best time of day</legend>
        <div className="grid gap-2.5 sm:grid-cols-3">
          {TIME_BLOCKS.map((t) => (
            <label key={t} className={`flex cursor-pointer items-center justify-center rounded-md border px-3 py-3 text-center font-semibold min-h-[48px] transition-colors ${
              form.time_block === t ? "border-navy bg-navy text-white" : "border-input bg-white text-ink hover:border-navy"
            }`} data-testid={`schedule-time-${t.split(" ")[0].toLowerCase()}`}>
              <input type="radio" name="time_block" className="sr-only" checked={form.time_block === t}
                onChange={() => { setForm({ ...form, time_block: t }); setErrors((p) => ({ ...p, time_block: "" })); }} />
              {t}
            </label>
          ))}
        </div>
        <FieldError message={errors.time_block} />
      </fieldset>

      <div className="mt-6">
        <label className="field-label" htmlFor="sf-notes">Anything you'd like Michael to know? (Optional)</label>
        <textarea id="sf-notes" className="field-input min-h-[96px]" value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="schedule-notes" />
      </div>

      <div className="mt-5">
        <ConsentCheckbox checked={consent} onChange={(v) => { setConsent(v); setErrors((p) => ({ ...p, consent: "" })); }} error={errors.consent} testid="schedule" />
      </div>
      <button type="submit" className="btn-accent mt-6 w-full sm:w-auto" disabled={submitting} data-testid="schedule-submit-button">
        {submitting ? "Sending…" : "Request a Call Back"}
      </button>
    </form>
  );
}
